import { ProjectListCard } from '@aecom-ai-engineering/react-components';
import { Project } from '@consigli/types';
import { type FC, type ReactNode } from 'react';

import { ProjectCardLink } from './project-card-link';
import { type ProjectView } from './use-project-view';

interface ProjectGridProps {
  projects: Project[];
  view: ProjectView;
  /** Appended after the cards, so loading placeholders continue the same rows. */
  children?: ReactNode;
}

/**
 * The rendered width of the card image, for `sizes`. Worked from the columns
 * below: the 1136px content column, 32px gutters either side, the 24px gap, and
 * the side nav at its collapsed 56px, the width that makes a card widest.
 */
const GRID_IMAGE_SIZES = [
  '(min-width: 1280px) 266px',
  '(min-width: 1024px) calc((100vw - 168px) / 3)',
  '(min-width: 768px) calc((100vw - 144px) / 2)',
  'calc(100vw - 120px)',
].join(', ');

/** ProjectListCard.css fixes the thumbnail at space-28 + space-2. */
const LIST_IMAGE_SIZES = '120px';

/**
 * The redesigned project grid. Columns and gap only — the page owns the content
 * width, so this stays usable at whatever width it is given.
 *
 * Not `CardGrid`: that is shared with the service list, and its `2xl:grid-cols-6`
 * step collapses to 178px tracks once the side nav takes 240px. The two-column
 * step is `md` rather than `sm` here, since 640px minus the nav was never a real
 * two-column width.
 *
 * The list view is the same grid at one column, with no gap, so the line along
 * ProjectListCard's bottom edge separates one row from the next.
 */
export const ProjectGrid: FC<ProjectGridProps> = ({ projects, view, children }) => (
  <div
    className={
      view === 'list'
        ? 'grid grid-cols-1'
        : 'grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
    }
  >
    {projects.map((project) => (
      <ProjectCardLink
        card={view === 'list' ? ProjectListCard : undefined}
        imageSizes={view === 'list' ? LIST_IMAGE_SIZES : GRID_IMAGE_SIZES}
        key={project.id}
        project={project}
      />
    ))}
    {children}
  </div>
);
